import { useMutation } from '@apollo/client';
import { toast } from '@crehana/compass.ui/toaster';
import { useTranslation } from '@crehana/i18n';
import { date } from '@crehana/utils';
import { useRouter } from 'next/router';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';

import { REPORT_TYPE_EVENT } from '../../pages/Reports/amplitude';
import useAmplitudeLog from '../useAmplitudeLog';
import type { AmplitudeEventProps } from '../useAmplitudeLog/useAmplitudeLog';
import useDashboardMetrics from '../useDashboardMetrics';
import ANALYTICS_REPORT_QUERY from './graphql/analytics-report.v5.graphql';
import type {
  Analytics_report,
  Analytics_reportVariables,
} from './graphql/types/Analytics_report';

enum STATUS {
  SUCCESS = 'success',
  FAILED = 'error',
}

export const useAnalyticsReport = (slug: string) => {
  const {
    currentUserOrganization,
    organizationId,
    isCorporation,
    hasStudioAvailable,
  } = useAdminDashboard();
  const { activityOrganization } = useDashboardMetrics({ slug });
  const router = useRouter();
  const { t } = useTranslation();
  const { amplitudeEvent } = useAmplitudeLog();
  const [generateAnalyticsReport, { loading }] = useMutation<
    Analytics_report,
    Analytics_reportVariables
  >(ANALYTICS_REPORT_QUERY, {
    context: { clientName: 'v5' },
  });

  const sendAmplitudeEvent = (
    variables: Omit<Analytics_reportVariables['input'], 'slug'>,
    data?: Analytics_report | null,
    dataOverride?: AmplitudeEventProps,
  ) => {
    if (dataOverride) {
      return amplitudeEvent(dataOverride);
    }

    const { report_type, ...restVariables } = variables;
    const defaultAmplitudeData = {
      'Month Date': date.getMonthName(),
      'Day of the week': date.format(new Date(), 'dddd'),
      'Report Type': REPORT_TYPE_EVENT[report_type].reportType,
      'Report Filters': restVariables,
      'Organization Session': router.asPath.split('/')[2],
      user: {
        'Current User ORG': currentUserOrganization,
        'Organization Type': isCorporation,
        'Organization Id': organizationId,
        'Organization Slug': slug,
        'Start Date': activityOrganization?.activityPrevious[0]?.date,
        'Has Classroom': hasStudioAvailable,
      },
      status:
        data?.analytics_report?.report_organization_progress_call.__typename ===
          'ReportResponse' &&
        data?.analytics_report?.report_organization_progress_call.success
          ? STATUS.SUCCESS
          : STATUS.FAILED,
    };

    const amplitudeData =
      data?.analytics_report?.report_organization_progress_call.__typename !==
      'ReportResponse'
        ? {
            ...defaultAmplitudeData,
            errors:
              data?.analytics_report?.report_organization_progress_call
                .__typename === 'AnalyticsMutationInternalError'
                ? data?.analytics_report?.report_organization_progress_call
                    .errors
                : [
                    data?.analytics_report?.report_organization_progress_call
                      .error || 'Report already in progress',
                  ],
          }
        : defaultAmplitudeData;

    amplitudeEvent({
      event: REPORT_TYPE_EVENT[report_type].event,
      data: amplitudeData,
    });
  };

  const submitReport = async (
    variables: Omit<Analytics_reportVariables['input'], 'slug'>,
    amplitude?: AmplitudeEventProps,
  ) => {
    try {
      const { data } = await generateAnalyticsReport({
        variables: {
          input: {
            ...variables,
            slug,
          },
        },
      });

      sendAmplitudeEvent(variables, data, amplitude);

      const responseType =
        data?.analytics_report?.report_organization_progress_call.__typename;

      if (
        responseType === 'ReportResponse' &&
        data?.analytics_report?.report_organization_progress_call.success
      ) {
        toast.success(t('SNACKBAR_DEFAULT_SUCCESS'));
        return;
      }

      if (responseType === 'ReportExistsResponse') {
        toast.error(t('SNACKBAR_REPORT_EXISTS_ERROR'), {
          duration: 5000,
          titleClassName: 'tw-line-clamp-none',
        });
        return;
      }

      if (responseType === 'AnalyticsMutationInternalError') {
        toast.error(t('SNACKBAR_DEFAULT_ERROR'));
        return;
      }

      if (
        responseType === 'ReportResponse' &&
        !data?.analytics_report?.report_organization_progress_call.success
      ) {
        toast.error(t('SNACKBAR_DEFAULT_ERROR'));
        return false;
      }

      return data;
    } catch (err) {
      console.error(err);
      toast.error(t('SNACKBAR_DEFAULT_ERROR'));
      return false;
    }
  };

  return {
    submitReport,
    loading,
  };
};
